import axios, { AxiosResponse } from 'axios';

import { MempoolFees, UniversalCache } from '../../common';

/**
 * Returns the current fee estimate in sat/vbyte from an API
 * Flattens potential spikes in the best fees by averaging the best n fee estimates from the API
 */
type MempoolFeeEstimate = {
  fastestFee: number;
  halfHourFee: number;
  hourFee: number;
  economyFee: number;
  minimumFee: number;
};

type MempoolFeeBlock = {
  blockSize: number;
  blockVSize: number;
  nTx: number;
  totalFees: number;
  medianFee: number;
  feeRange: number[];
};

const MEMPOOL_FEES_DATA_STORAGE = 'mempoolFeesDataStorage';
const MEMPOOL_FEES_CACHE_DURATION = 12 * 1000; // 12 seconds
const mempoolFeesCache = new UniversalCache<MempoolFees>(MEMPOOL_FEES_DATA_STORAGE);

export const getSatPerVbEstimateFee = async (mempoolApiEndpoint: string): Promise<MempoolFees> => {
  const cachedEntry = mempoolFeesCache.get(MEMPOOL_FEES_DATA_STORAGE);
  if (cachedEntry && Date.now() - cachedEntry.timestamp < MEMPOOL_FEES_CACHE_DURATION) {
    return cachedEntry.data;
  }

  const mempoolFeeRecommended: AxiosResponse<MempoolFeeEstimate> = await axios.get(
    `${mempoolApiEndpoint}/v1/fees/recommended`
  );

  const mempoolFeeBlocks = await axios.get<MempoolFeeBlock[]>(`${mempoolApiEndpoint}/v1/fees/mempool-blocks`);
  const currentMempoolBlock = mempoolFeeBlocks.data[0];
  const { medianFee, feeRange } = currentMempoolBlock;
  const lowestFee = feeRange[0];

  const { fastestFee, minimumFee, economyFee } = mempoolFeeRecommended.data;

  // Sanity check: Ensure mempool block fees don't exceed recommended fees
  const safeMedianFee =
    medianFee && medianFee > 0 && medianFee < fastestFee ? medianFee : economyFee && economyFee > 0 ? economyFee : 1;
  const safeLowestFee =
    lowestFee && lowestFee > 0 && lowestFee < fastestFee ? lowestFee : minimumFee && minimumFee > 0 ? minimumFee : 1;

  const currentMempoolBlockFees = {
    economyFee: safeMedianFee,
    minimumFee: safeLowestFee,
    fastestFee: fastestFee && fastestFee > 0 ? fastestFee : 2,
  };

  mempoolFeesCache.set(MEMPOOL_FEES_DATA_STORAGE, currentMempoolBlockFees);
  return currentMempoolBlockFees;
};
